import { useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'preference_smartApplyVariant'
const VARIANTS = ['variant_a'] as const

export type SmartApplyVariant = typeof VARIANTS[number]

export function useSmartApplyVariant() {
  const [variant, setVariant] = useState<SmartApplyVariant>(() => {
    return getOrAssignVariant()
  })

  useEffect(() => {
    const storedVariant = getOrAssignVariant()
    if (storedVariant !== variant) {
      setVariant(storedVariant)
    }
  }, [variant])

  const getExperimentVariant = useMemo(() => {
    return () => variant
  }, [variant])

  return {
    variant,
    getExperimentVariant
  }
}

function getOrAssignVariant(): SmartApplyVariant {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)

    if (stored === 'variant_b' || stored === 'variant_c') {
      localStorage.setItem(STORAGE_KEY, 'variant_a')
      return 'variant_a'
    }

    if (stored && isValidVariant(stored)) {
      return stored
    }

    return assignRandomVariant()
  } catch (error) {
    console.warn('Failed to access localStorage for SmartApply variant, using random assignment:', error)
    return assignRandomVariant()
  }
}

function assignRandomVariant(): SmartApplyVariant {
  const randomIndex = Math.floor(Math.random() * VARIANTS.length)
  const newVariant = VARIANTS[randomIndex]

  try {
    localStorage.setItem(STORAGE_KEY, newVariant)
  } catch (error) {
    console.warn('Failed to save SmartApply variant to localStorage:', error)
  }

  return newVariant
}

function isValidVariant(value: string): value is SmartApplyVariant {
  return VARIANTS.includes(value as SmartApplyVariant)
}

export function getCurrentVariant(): SmartApplyVariant | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored && isValidVariant(stored) ? stored : null
  } catch {
    return null
  }
}

export function clearVariant(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear SmartApply variant from localStorage:', error)
  }
}

export function isInExperiment(): boolean {
  return getCurrentVariant() !== null
}
